import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { wellnessDomains, programPhases, wellnessImages, siteConfig } from '../data/mock';
import * as LucideIcons from 'lucide-react';

const ProgramPage = () => {
  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className="w-7 h-7" /> : null;
  };

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section with Logo - Centered and Smaller */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[900px] h-[900px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white/95 backdrop-blur-sm p-8 rounded-lg border-2 border-[#1E3A8A]">
              <p className="text-[#E31837] font-semibold text-base mb-3 tracking-wide uppercase">12-Week Transformation</p>
              <h1 className="text-[#1E3A8A] text-2xl md:text-3xl font-bold mb-4">
                The <span className="text-[#E31837]">7-Domain Wellness Wheel</span> Program
              </h1>
              <p className="text-[#1E3A8A] text-lg mb-6">
                A comprehensive coaching program that addresses every aspect of your well-being, 
                from physical health to spiritual fulfillment.
              </p>
              <a href={siteConfig.jotformIntakeLink} target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary text-base">
                  Begin Your Journey
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 7 Domains Detail Section */}
      <section id="domains" className="section-padding bg-[#F8F9FA] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[1000px] h-[1000px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-[#1E3A8A] mb-6">
              The 7 Domains of <span className="text-[#E31837]">Wellness</span>
            </h2>
            <p className="text-[#1E3A8A] text-xl max-w-2xl mx-auto">
              Each domain interconnects to create your complete wellness picture
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {wellnessDomains.map((domain) => (
              <Card
                key={domain.id}
                className="card-bordered bg-white/95 backdrop-blur-sm"
              >
                <CardHeader>
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 rounded-lg border-2 border-[#1E3A8A] flex items-center justify-center bg-white">
                      <span className="text-[#1E3A8A]">
                        {getIcon(domain.icon)}
                      </span>
                    </div>
                    <CardTitle className="text-[#1E3A8A] text-2xl">{domain.name}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-[#E31837] font-semibold mb-2">Focus Areas</p>
                      <p className="text-[#1E3A8A]">{domain.focusAreas}</p>
                    </div>
                    <div>
                      <p className="text-[#E31837] font-semibold mb-2">What We Address</p>
                      <p className="text-[#1E3A8A]">{domain.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Program Phases Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[1100px] h-[1100px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-[#1E3A8A] mb-6">
              12-Week <span className="text-[#E31837]">Program Structure</span>
            </h2>
            <p className="text-[#1E3A8A] text-xl max-w-2xl mx-auto">
              Three distinct phases designed to build lasting transformation
            </p>
          </div>

          <Tabs defaultValue="phase-1" className="w-full">
            <TabsList className="flex justify-center bg-white/95 border-2 border-[#1E3A8A] p-2 rounded-lg mb-12 backdrop-blur-sm">
              {programPhases.map((phase) => (
                <TabsTrigger
                  key={phase.id}
                  value={`phase-${phase.id}`}
                  className="px-8 py-4 text-base font-semibold data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white rounded transition-all"
                >
                  {phase.phase}
                </TabsTrigger>
              ))}
            </TabsList>

            {programPhases.map((phase) => (
              <TabsContent key={phase.id} value={`phase-${phase.id}`}>
                <div className="grid lg:grid-cols-2 gap-16 items-start">
                  <div className="bg-white/95 backdrop-blur-sm p-10 rounded-lg border-2 border-[#1E3A8A]">
                    <span className="inline-block bg-[#E31837] text-white px-4 py-2 rounded font-semibold mb-6">
                      {phase.weeks}
                    </span>
                    <h3 className="text-[#1E3A8A] text-3xl font-bold mb-6">
                      {phase.title}
                    </h3>
                    <p className="text-[#1E3A8A] text-xl mb-8">{phase.focus}</p>

                    <div className="space-y-5 mb-10">
                      {phase.details.map((detail, index) => (
                        <div key={index} className="flex items-start gap-4">
                          <CheckCircle className="w-6 h-6 text-[#E31837] mt-1 flex-shrink-0" />
                          <span className="text-[#1E3A8A] text-lg">{detail}</span>
                        </div>
                      ))}
                    </div>

                    <div className="card-bordered p-6 bg-white">
                      <p className="text-[#E31837] font-semibold mb-2">Deliverable</p>
                      <p className="text-[#1E3A8A] font-bold text-lg">{phase.deliverable}</p>
                    </div>
                  </div>

                  <div className="relative hidden lg:block">
                    <div className="border-4 border-[#1E3A8A] rounded-lg overflow-hidden shadow-2xl">
                      <img
                        src={wellnessImages.temple}
                        alt="Coaching Phase"
                        className="w-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Timeline Visual - With Silhouette */}
      <section className="section-padding bg-[#F8F9FA] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[900px] h-[900px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          {/* Silhouette Container */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl border-4 border-[#1E3A8A] p-12 shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)]">
            <h2 className="text-[#1E3A8A] text-center mb-16">
              Your <span className="text-[#E31837]">Journey Timeline</span>
            </h2>

            <div className="relative">
              <div className="absolute top-10 left-0 right-0 h-1 bg-[#1E3A8A] hidden md:block" />

              <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                {[
                  { week: "Week 1", title: "Baseline Intake", desc: "Comprehensive assessment" },
                  { week: "Weekly", title: "Check-Ins", desc: "Progress monitoring" },
                  { week: "Week 6", title: "Mid-Program", desc: "Assessment & adjustments" },
                  { week: "Week 12", title: "Graduation", desc: "Final results" },
                  { week: "Ongoing", title: "Maintenance", desc: "Continued support" },
                ].map((item, index) => (
                  <div key={index} className="relative">
                    <div className="w-5 h-5 bg-[#E31837] rounded-full mx-auto mb-6 relative z-10 border-4 border-white hidden md:block" />
                    <div className="bg-white border-4 border-[#1E3A8A] rounded-lg p-6 text-center hover:shadow-lg transition-all">
                      <span className="text-[#E31837] font-semibold">{item.week}</span>
                      <h4 className="text-[#1E3A8A] font-bold text-lg mt-2 mb-2">{item.title}</h4>
                      <p className="text-[#1E3A8A]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - With Silhouette */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[900px] h-[900px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl border-4 border-[#1E3A8A] p-12 shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)] text-center">
              <img
                src={siteConfig.logo}
                alt={siteConfig.name}
                className="h-20 w-20 object-contain mx-auto mb-8"
              />
              <h2 className="text-[#1E3A8A] mb-8">
                Start Your 12-Week Transformation
              </h2>
              <p className="text-[#1E3A8A] text-xl mb-10">
                Take the first step toward whole-person wellness.
              </p>
              <a href={siteConfig.jotformIntakeLink} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#1E3A8A] text-white border-4 border-[#1E3A8A] hover:bg-[#E31837] hover:border-[#E31837] text-lg px-10 py-4 rounded font-semibold transition-all">
                  Apply Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramPage;
